import React from "react";
import { Logo } from "../components/Logo";
import { useAuth } from "../components/AuthContext";
import { Lock, User } from "lucide-react";

const formatErrorDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      const msg = formatErrorDetail(err?.response?.data?.detail) || "Connexion impossible";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-5 py-8 stage-glow">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm bg-[#0d0d0d] border border-white/10 rounded-2xl p-6 space-y-5"
        data-testid="login-form"
      >
        <div className="flex justify-center pb-2">
          <Logo size="lg" />
        </div>
        <div className="text-center">
          <div className="text-base font-bold text-white">Accès protégé</div>
          <div className="text-xs text-neutral-400 mt-1">
            Entre tes identifiants pour accéder à GiGLife26.
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/80 font-bold flex items-center gap-1.5">
              <User size={12} /> Nom d'utilisateur
            </label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 w-full bg-black border-2 border-white/15 rounded-lg px-3 py-3 text-base font-bold text-white focus:outline-none focus:border-[var(--gl-gold)]"
              data-testid="login-username-input"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/80 font-bold flex items-center gap-1.5">
              <Lock size={12} /> Mot de passe
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-black border-2 border-white/15 rounded-lg px-3 py-3 text-base font-bold text-white focus:outline-none focus:border-[var(--gl-gold)]"
              data-testid="login-password-input"
            />
          </div>
        </div>

        {error && (
          <div
            className="text-sm text-orange-400 bg-orange-500/10 border border-orange-500/40 rounded-lg px-3 py-2"
            data-testid="login-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || !username || !password}
          className="w-full tap-lg rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="login-submit-btn"
        >
          {busy ? "Connexion…" : "Se connecter"}
        </button>

        <div className="text-[10px] text-neutral-500 text-center leading-snug">
          Accès temporairement protégé.
        </div>
      </form>
    </div>
  );
}
